import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ListViewApiResponse } from '../interfaces';
import { transformResortData } from '../util';
import { invokeTypedService } from '../services';
import { SEARCH_API_ROUTES } from '../constant/URL';
import { getUserData } from "../util/index";
import { logErrorToSplunk } from '../../../service';
import { updateMapResortWishlistStatus } from './mapSlice';
import { configuredAEMUrl } from '../../../utils/AEMUtils';
import { GET_OFFER_DETAILS } from '../../../utils/AEMUrls';

// import { filterOutRegionDimensions } from '../component/FilterPanel/Util/FilterUtil';
import type {
  SearchCriteriaElements,
  ListViewState,
  SearchAPIParams,
  SearchAPIResponse,
  WishlistAPIParams,
  RemoveWishlistAPIParams,
  LocationObject,
  GoogleDetailResponse,
  LocationData,
  DynamicFilterCalendarPayload,
  HotelAvailabilityResponse,
} from './types';
// import { logErrorMessage } from '../../../common/actions';
 
// ✅ Updated interfaces to match actual API response structure
export interface OfferDetail {
  offerId: string;
  offerName: string;
  offerDesc?: string | null;
  // Add other offer properties as needed
}

// ✅ The API returns this structure directly
export interface OfferDetailsResponse {
  offerDetails: {
    offersList: OfferDetail[];
    rentalDataJsons: null; // Based on screenshot
  };
}

// Initial state
const initialState: ListViewState = {
  resortData: [],
  totalResortCount: 0,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  errorShowMore: null,
  lastSearchCriteria: null,
  wishlistLoading: {},
  wishlistError: null,
  mapCardWishListFlag: false,
  allowedDates: [],
  calendarLoading: false,
  calendarError: null,
  progressiveDateBarData: [],
  // Month-specific calendar data
  allowedMonths: [],
  monthCalendarLoading: false,
  monthCalendarError: null,
  progressiveMonthBarData: [],
  isDateTabActive: true,
  isMonthTabActive: false,
  hotelData: [],
  afterKey: {},
};
 
// Helper functions to build wishlist API URLs
function buildWishlistUrl(apiPath: string): string {
  const userData = getUserData();
  const customerId = userData.memberId;
  const queryParams = new URLSearchParams({
    customerIdQ: customerId
  });
  return `${apiPath}?${queryParams.toString()}`;
}
 
const buildAddToWishlistUrl = () => buildWishlistUrl(SEARCH_API_ROUTES.ADD_TO_WISHLIST);
const buildRemoveFromWishlistUrl = () => buildWishlistUrl(SEARCH_API_ROUTES.REMOVE_FROM_WISHLIST);
 
const buildDynamicFilterUrl = (apiPath: string): string => {
  const userData = getUserData();
  const customerId = userData.memberId;
  const queryParams = new URLSearchParams({
    customerIdQ: customerId,
    appType: 'rcio',
  });
  return `${apiPath}?${queryParams.toString()}`;
}
 
// this function is used for local modification of the wishlist flag in resortData object(Used for -->updateWishlistStatus.fulfilled  && removeFromWishlistStatus.fulfilled)
function setResortFavoriteStatus(state: ListViewState, resortId: string, isFavorite: boolean) {
  const resortIndex = state.resortData.findIndex(resort => resort.resourceKey === resortId);
  if (resortIndex !== -1) {
    const resort = state.resortData[resortIndex];
    if (resort) {
      resort.isEnablewksFavoritedResort = isFavorite;
    }
  }
}
 
// const serviceUrl = window.weeksWebCloudConfig?.REACT_APP_SERVICE_URL || '/api';
// const serviceUrlNew = window.weeksWebCloudConfig?.REACT_APP_NEW_VERSION || 'https://servicesdc.b2c.rci.com/resort-operations/v1';
const DYNAMIC_FILTER_CALENDAR_URL = SEARCH_API_ROUTES.DYNAMIC_FILTER_CALENDAR;
 
 
// Async thunk for calling the search API
export const syncSearchCriteria = createAsyncThunk<void, SearchCriteriaElements>(
  'listView/syncSearchCriteria',
  async (searchData: SearchCriteriaElements, { rejectWithValue }) => {
    // Extract data from searchData for query parameters
    const filters = searchData.filters || [];
    const resortId = ''; // Add logic if needed
    const offerId = searchData.searchCriteria?.offerId || '';
    const minDate = searchData.searchCriteria?.minStartDate || '';
    const maxDate = searchData.searchCriteria?.maxStartDate || '';
    const selDepositId = searchData.selectedDeposit || '';

    // Build query parameters
    const queryParams = new URLSearchParams({
      filters: JSON.stringify(filters),
      resortId: resortId,
      offerId: offerId,
      minDate: minDate,
      maxDate: maxDate,
      selDepositId: selDepositId
    });

    const syncUrl = `/weeks-search/syncSearchCriteria?${queryParams.toString()}`;

    try {
      await invokeTypedService({
        resource: syncUrl,
        method: 'GET',
        replaceUrlWithResource: true,
      });
     
      return;
    } catch (syncError) {
      console.warn('syncSearchCriteria API failed:', syncError);
      // Log error to Splunk
      try {
        logErrorToSplunk(syncError, syncUrl, 'GET', 'ListView - syncSearchCriteria');
      } catch (logError) {
        console.error('Error logging failed:', logError);
      }
      return rejectWithValue((syncError as Error).message);
    }
  }
);

// Async thunk for calling the search API
export const callSearchAPI = createAsyncThunk<SearchAPIResponse, SearchAPIParams | SearchCriteriaElements>(
  'listView/callSearchAPI',
  async (params: SearchAPIParams | SearchCriteriaElements, { rejectWithValue, dispatch, signal }) => {
    // Handle both old and new parameter formats for backward compatibility
    const checkInDate = 'searchCriteria' in params && params.searchCriteria ? params.searchCriteria.minStartDate : '';
    const checkOutDate = 'searchCriteria' in params && params.searchCriteria ? params.searchCriteria.maxStartDate : '';
    const searchData = 'searchData' in params ? params.searchData : params;
    const appendToExisting = 'appendToExisting' in params ? params.appendToExisting : false;
    const LISTVIEW_URL = `${SEARCH_API_ROUTES.LISTVIEW_URL}?customerIdQ=${getUserData().memberId}&appType=rcio`;

    try {
      const response = await invokeTypedService<ListViewApiResponse>({
        resource : `${LISTVIEW_URL}`,
        data : JSON.stringify(searchData),
        method: 'POST',
      });

      // If the thunk was superseded by a newer dispatch, discard this result
      if (signal.aborted) {
        return rejectWithValue('cancelled');
      }

      // Extract the actual data from the typed service response (guard against 200-with-empty/missing body)
      const result: ListViewApiResponse = (response?.data as ListViewApiResponse) || ({} as ListViewApiResponse);
     
      // Transform and return the resort data (translation parameter is optional, fallback will be used)
      const transformedResorts = transformResortData(result.availableResorts || []);
      // if result has aimsSearchId, dispatch fetchARNPricingForAIMSResorts
      if (result.aimsSearchId) {
        dispatch(fetchARNPricingForAIMSResorts(result));
      }
      // UPDATED: Pass the checkInDate and checkOutDate to hotel pricing function
      if (result.hotels && result.hotels.length > 0) {
        dispatch(fetchHotelPricingForHotels({
          result,
          checkInDate,
          checkOutDate
        }));
      } 
      // else {
      //   console.log('No hotels found in search results');
      // }

      // 🔥 UPDATED: Use the new syncSearchCriteria function
      // dispatch(syncSearchCriteria(searchData));

      return {
        resortData: transformedResorts,
        totalResortCount: result?.availableResortCount || 0,
        searchCriteria: searchData,
        appendToExisting: appendToExisting,
        hotelData: result?.hotels || [],
        afterKey: result?.afterKey || {},
      };

    } catch (error) {
      // Log error to Splunk
      try {
        logErrorToSplunk(error, LISTVIEW_URL, 'POST', 'ListView - callSearchAPI');
      } catch (logError) {
        console.error('Error logging failed:', logError);
      }
      return rejectWithValue((error as Error).message);
    }
  }
);
 
export const fetchARNPricingForAIMSResorts = createAsyncThunk(
  'listView/fetchARNPricingForAIMSResorts',
  async (resort_list: ListViewApiResponse, { rejectWithValue }) => {
    try {
      // Validate input data
      if (!resort_list || !resort_list.aimsSearchId) {
        return rejectWithValue('Invalid resort_list data - missing aimsSearchId');
      }
 
      // Check if there are resorts with virtualAiResort set to 'Y'
      const hasVirtualAiResort = resort_list?.availableResorts?.some(
        (resort) => resort.virtualAiResort === 'Y'
      );
     
      if (!hasVirtualAiResort) {
        return rejectWithValue('No resorts with virtualAiResort set to "Y"');
      }
 
      // Get service URL from config
      // const serviceUrl = window.weeksWebCloudConfig?.REACT_APP_SERVICE_URL;
     
      // if (!serviceUrl) {
      //   throw new Error('Service URL not configured');
      // }
 
      // Make API call using your existing service
      const response = await invokeTypedService({
        resource: `${SEARCH_API_ROUTES.GET_ARN_PRICING}&searchId=${resort_list.aimsSearchId}`,
        method: 'GET',
      });
 
      // Check response
      if (!response || !response.data) {
        throw new Error('No data available from ARN pricing service');
      }
 
      // console.log('ARN Pricing API response:', response.data);
      return response.data;
 
    } catch (error) {
      console.error('Error fetching ARN pricing:', error);
      return rejectWithValue((error as Error).message || 'Error fetching ARN pricing data');
    }
  }
);
 
// UPDATED: Modified fetchHotelPricingForHotels to accept checkInDate and checkOutDate as parameters
export const fetchHotelPricingForHotels = createAsyncThunk(
  'listView/fetchHotelPricingForHotels',
  async (
    {
      result,
      checkInDate,
      checkOutDate
    }: {
      result: ListViewApiResponse;
      checkInDate: string;
      checkOutDate: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // Validate input data
      if (!result || !result.hotels || result.hotels.length === 0) {
        return rejectWithValue('No hotel data available for pricing');
      }
     
      const hotelIds = result.hotels
        .map(hotel => hotel.PropertyId)
        .filter(Boolean);
 
      if (hotelIds.length === 0) {
        return rejectWithValue('No valid hotel PropertyIds found for pricing');
      }
 
      // Get user data for the API call
      const userData = getUserData();
      const customerId = userData.memberId;
      const currency = userData?.currencies?.[0]?.code;
 
      // Use the passed dates or fallback to hotel data or default dates
      const finalCheckInDate = checkInDate;
      const finalCheckOutDate = checkOutDate;
     
      // Build the API URL with proper encoding
      const queryParams = new URLSearchParams({
        customerIdQ: customerId,
        hotelIds: hotelIds.join(','), // URLSearchParams will handle encoding
        checkInDate: finalCheckInDate,
        checkOutDate: finalCheckOutDate,
        currency: currency
      });
 
      const fullUrl = `${SEARCH_API_ROUTES.GET_HOTEL_PRICING}?${queryParams.toString()}`;
 
 
      // Make API call using invokeTypedService
      const response = await invokeTypedService<HotelAvailabilityResponse>({
        resource: fullUrl,
        method: 'GET',
      });
     
      // Check response
      if (!response || !response.data) {
        throw new Error('No data available from hotel availability service');
      }
 
      // console.log('Hotel Availability API response:', response.data);
     
      // Transform the response to match our expected format
      const transformedResponse = {
        hotelAvailabilityDetails: response.data.hotels || [],
        searchId: result.aimsSearchId,
        requestedHotelIds: hotelIds,
        checkInDate: finalCheckInDate,
        checkOutDate: finalCheckOutDate
      };
 
      return transformedResponse;
 
    } catch (error) {
      console.error('Error fetching hotel availability/pricing:', error);
      return rejectWithValue((error as Error).message || 'Error fetching hotel pricing data');
    }
  }
);
// Async thunk for updating wishlist status
export const updateWishlistStatus = createAsyncThunk<void, WishlistAPIParams>(
  'listView/updateWishlistStatus',
  async ({ resortId }, { rejectWithValue, dispatch }) => {
    try {
      const url = buildAddToWishlistUrl();
      const response = await invokeTypedService({
        resource: url,
        method: 'POST',
        data: { resortId },
        // replaceUrlWithResource: true
      });
 
      if (response.status === 200) {
        dispatch(updateMapResortWishlistStatus({ resortId, isFavorited: true }));
        return;
      } else {
        throw new Error(`Failed to add resort to wishlist: ${response.status} ${response}`);
      }
    } catch (error) {
      console.error('Error adding resort to wishlist:', error);
      return rejectWithValue((error as Error).message);
    }
  }
);
 
// Async thunk for removing from wishlist (PUT request)
export const removeFromWishlistStatus = createAsyncThunk<void, RemoveWishlistAPIParams>(
  'listView/removeFromWishlistStatus',
  async ({ resortId }, { rejectWithValue, dispatch }) => {
    try {
      const url = buildRemoveFromWishlistUrl();
      const response = await invokeTypedService({
        resource: url,
        method: 'PUT',
        data: { resortId },
        // contentType: 'application/json',
        // replaceUrlWithResource: true
      });
 
      if (response.status == 200) {
        dispatch(updateMapResortWishlistStatus({ resortId, isFavorited: false }));
        return;
      } else {
        throw new Error(`Failed to remove resort from wishlist: ${response.status} ${response}`);
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return rejectWithValue((error as Error).message);
    }
  }
);
 
// Async thunk for fetching dynamic filter calendar dates
export const callForDynamicFilterCalendar = createAsyncThunk<
  { calendarData: Array<{ name: string; availableResortCount: number; availableInventoryCount: number; url: string }>; allowedDates: string[] },
  { payload: DynamicFilterCalendarPayload }
>(
  'listView/callForDynamicFilterCalendar',
  async ({ payload }, { rejectWithValue }) => {
    try {
      // console.log('Fetching dynamic filter calendar - API URL:', DYNAMIC_FILTER_CALENDAR_URL);
      // console.log('Request payload:', payload);
 
      // Define proper response type matching the API response
      interface CalendarValue {
        name: string;
        availableResortCount: number;
        availableInventoryCount: number;
        url: string;
      }
 
      interface CalendarResponse {
        name: string;
        values: CalendarValue[];
      }
 
      const response = await invokeTypedService<CalendarResponse>({
        resource: buildDynamicFilterUrl(DYNAMIC_FILTER_CALENDAR_URL),
        method: 'POST',
        data: JSON.stringify(payload),
      });
 
      if (response.status === 200 && response.data) {
        // Store the full calendar data with resort counts
        const calendarData = response.data.values || [];
       
        // Extract allowed dates from the response and sort them
        const allowedDates = (response.data.values?.map(item => {
          // Convert MM-DD-YYYY to YYYY-MM-DD format
          const [month, day, year] = item.name.split('-');
          return `${year}-${month?.padStart(2, '0')}-${day?.padStart(2, '0')}`;
        }) || []).sort((a, b) => {
          return new Date(a).getTime() - new Date(b).getTime();
        });
        return { calendarData, allowedDates };
      } else {
        // invokeTypedService does not throw — it returns the real axios error on `response.error`.
        // Forward that axios error (which carries `config.data` = the request payload and the
        // server response) so logErrorToSplunk can capture the payload for this API.
        throw response.error || new Error(`Failed to fetch dynamic filter calendar: ${response.status}`);
      }
    } catch (error) {
      // Log error to Splunk
        try {
              logErrorToSplunk(error, buildDynamicFilterUrl(DYNAMIC_FILTER_CALENDAR_URL), 'POST', 'listView - callForDynamicFilterCalendar');
            } catch (logError) {
              console.error('Error logging failed:', logError);
            }
      console.error('Error fetching dynamic filter calendar:', error);
      return rejectWithValue((error as Error).message);
    }
  }
);
 
// Async thunk for fetching dynamic filter calendar dates for MONTHS
export const callForDynamicFilterMonthCalendar = createAsyncThunk<
  { calendarData: Array<{ name: string; availableResortCount: number; availableInventoryCount: number; url: string }>; allowedMonths: string[] },
  { payload: DynamicFilterCalendarPayload }
>(
  'listView/callForDynamicFilterMonthCalendar',
  async ({ payload }, { rejectWithValue }) => {
    try {
      // console.log('Fetching dynamic filter MONTH calendar - API URL:', DYNAMIC_FILTER_CALENDAR_URL);
      // console.log('Request payload:', payload);
 
      // Define proper response type matching the API response
      interface CalendarValue {
        name: string;
        availableResortCount: number;
        availableInventoryCount: number;
        url: string;
      }
 
      interface CalendarResponse {
        name: string;
        values: CalendarValue[];
      }
 
      const response = await invokeTypedService<CalendarResponse>({
        resource: buildDynamicFilterUrl(DYNAMIC_FILTER_CALENDAR_URL),
        method: 'POST',
        data: JSON.stringify(payload),
      });
 
      if (response.status === 200 && response.data) {
        // Store the full month calendar data with resort counts
        const calendarData = response.data.values || [];
       
        // Extract allowed months from the response (format: YYYYMM or YYYY-MM)
        const allowedMonths = (response.data.values?.map(item => {
          return item.name; // Keep the original format (YYYYMM)
        }) || []).sort((a, b) => {
          // Sort chronologically
          const yearMonthA = a.replace('-', '');
          const yearMonthB = b.replace('-', '');
          return yearMonthA.localeCompare(yearMonthB);
        });
        
        return { calendarData, allowedMonths };
      } else {
        // invokeTypedService does not throw — it returns the real axios error on `response.error`.
        // Forward that axios error (which carries `config.data` = the request payload and the
        // server response) so logErrorToSplunk can capture the payload for this API.
        throw response.error || new Error(`Failed to fetch dynamic filter month calendar: ${response.status}`);
      }
    } catch (error) {
      // Log error to Splunk
        try {
              logErrorToSplunk(error, buildDynamicFilterUrl(DYNAMIC_FILTER_CALENDAR_URL), 'POST', 'listView - callForDynamicFilterMonthCalendar');
            } catch (logError) {
              console.error('Error logging failed:', logError);
            }
      console.error('Error fetching dynamic filter month calendar:', error);
      return rejectWithValue((error as Error).message);
    }
  }
);

// Convert the function to TypeScript with proper typing
export const fetchLonLatData = async (
  placeid: string,
  obj: LocationObject
): Promise<LocationData> => {
  if (!placeid || !obj) {
    throw new Error('Place ID and object are required');
  }
 
  const resource = `/rci/destinations/typeahead/place-details?placeId=${placeid}&appType=rcio&customerIdQ=${getUserData().memberId}`;
 
  try {
    const response = await invokeTypedService<GoogleDetailResponse>({
      resource,
      method: 'GET',
    });
 
    if (!response.data) {
      throw new Error(`API error! status: ${response.status}`);
    }
   
    const data: GoogleDetailResponse = response.data;
 
    // Handle the new nested response structure
    if (data.status !== 'OK') {
      throw new Error(`Google API error: ${data.status}`);
    }
 
    if (!data.result?.geometry?.location) {
      throw new Error('Invalid response structure: missing location data');
    }
 
    const { lat, lng } = data.result.geometry.location;
 
    return {
      addlabel: obj.addlabel,
      displayAddLabel: obj.displayAddLabel,
      isPrevSearchResults: obj.isPrevSearchResults,
      label: obj.label,
      latitude: lat,
      longitude: lng,
      type: obj.type,
      typeName: obj.typeName,
    };
 
  } catch (error) {
    console.error('Google Detail Servlet API Error:', error);
    throw error;
  }
};
 
// Create the slice
const listViewSlice = createSlice({
  name: 'listView',
  initialState,
  reducers: {
    // Clear resort data
    clearResortData: (state) => {
      state.resortData = [];
      state.error = null;
    },    // Set loading state manually if needed
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // Set load more loading state manually if needed
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMore = action.payload;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Clear wishlist error
    clearWishlistError: (state) => {
      state.wishlistError = null;
    },
    // Clear allowed dates
    clearAllowedDates: (state) => {
      state.allowedDates = [];
      state.calendarError = null;
    },
    // Clear allowed months
    clearAllowedMonths: (state) => {
      state.allowedMonths = [];
      state.monthCalendarError = null;
    },
    // Set tab flags for date picker
    setTabFlags: (state, action: PayloadAction<'day' | 'month'>) => {
      state.isDateTabActive = action.payload === 'day';
      state.isMonthTabActive = action.payload === 'month';
    }
  },  extraReducers: (builder) => {
    builder
      .addCase(callSearchAPI.pending, (state, action) => {
        // Check if this is a load more request
        const appendToExisting = action.meta.arg && typeof action.meta.arg === 'object' && 'appendToExisting' in action.meta.arg ? action.meta.arg.appendToExisting : false;
       
        if (appendToExisting) {
          state.isLoadingMore = true;
        } else {
          // Keep existing resortData visible while loading so users see stale results
          // instead of a blank list. resortData is replaced on fulfilled.
          state.isLoading = true;
          state.isLoadingMore = false;
        }
        state.error = null;
        state.errorShowMore = null;
      })      
      .addCase(callSearchAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        // Check if we should append or replace data based on the appendToExisting flag
        const appendToExisting = action.meta.arg && typeof action.meta.arg === 'object' && 'appendToExisting' in action.meta.arg ? action.meta.arg.appendToExisting : false;
       
        if (appendToExisting) {
          // Append new resorts to existing array
          state.resortData = [...state.resortData, ...action.payload.resortData];
        } else {
          // Replace existing resorts with new data
          state.resortData = action.payload.resortData;
        }
       
        state.totalResortCount = action.payload.totalResortCount;
        state.lastSearchCriteria = action.payload.searchCriteria;
        state.hotelData = action.payload.hotelData;
        state.afterKey = action.payload.afterKey || {};
      })      
      .addCase(callSearchAPI.rejected, (state, action) => {
        // Ignore cancellations — a newer request is already in flight
        if (action.payload === 'cancelled') {
          return;
        }
        const appendToExisting = action.meta.arg && typeof action.meta.arg === 'object' && 'appendToExisting' in action.meta.arg ? action.meta.arg.appendToExisting : false;
        if (appendToExisting) {
          state.errorShowMore = action.payload as string;
        } else {
          state.error = action.payload as string;
        }
        state.isLoading = false;
        state.isLoadingMore = false;
      })
      // Wishlist API reducers with per-resort loading tracking
      .addCase(updateWishlistStatus.pending, (state, action) => {
        const resortId = action.meta.arg.resortId;
        state.wishlistLoading[resortId] = true;
        state.wishlistError = null;
      })
      .addCase(updateWishlistStatus.fulfilled, (state, action) => {
        const resortId = action.meta.arg.resortId;
        state.wishlistLoading[resortId] = false;
       
        setResortFavoriteStatus(state, resortId, true);
        state.mapCardWishListFlag = true;
      })
      .addCase(updateWishlistStatus.rejected, (state, action) => {
        const resortId = action.meta.arg.resortId;
        state.wishlistLoading[resortId] = false;
        state.wishlistError = action.payload as string;
      })
      // Remove from wishlist reducers with per-resort loading tracking
      .addCase(removeFromWishlistStatus.pending, (state, action) => {
        const resortId = action.meta.arg.resortId;
        state.wishlistLoading[resortId] = true;
        state.wishlistError = null;
      })
      .addCase(removeFromWishlistStatus.fulfilled, (state, action) => {
        const resortId = action.meta.arg.resortId;
        state.wishlistLoading[resortId] = false;
 
        setResortFavoriteStatus(state, resortId, false);
        state.mapCardWishListFlag = false;
      })
      .addCase(removeFromWishlistStatus.rejected, (state, action) => {
        const resortId = action.meta.arg.resortId;
        state.wishlistLoading[resortId] = false;
        state.wishlistError = action.payload as string;
      })
      // Dynamic filter calendar reducers
      .addCase(callForDynamicFilterCalendar.pending, (state) => {
        state.calendarLoading = true;
        state.calendarError = null;
        state.allowedDates = [];
      })
      .addCase(callForDynamicFilterCalendar.fulfilled, (state, action) => {
        state.calendarLoading = false;
        // Store the full calendar data with resort counts
        state.progressiveDateBarData = action.payload.calendarData;
       
        // Replace allowedDates with new data instead of merging
        state.allowedDates = action.payload.allowedDates.sort((a, b) => {
          return new Date(a).getTime() - new Date(b).getTime();
        });
      })
      .addCase(callForDynamicFilterCalendar.rejected, (state, action) => {
        state.calendarLoading = false;
        state.calendarError = action.payload as string;
      })
      // Dynamic filter MONTH calendar reducers
      .addCase(callForDynamicFilterMonthCalendar.pending, (state) => {
        state.monthCalendarLoading = true;
        state.monthCalendarError = null;
        state.allowedMonths = [];
      })
      .addCase(callForDynamicFilterMonthCalendar.fulfilled, (state, action) => {
        state.monthCalendarLoading = false;
        // Store the full month calendar data with resort counts
        state.progressiveMonthBarData = action.payload.calendarData;
       
        // Replace allowedMonths with new data
        state.allowedMonths = action.payload.allowedMonths.sort((a, b) => {
          // Sort chronologically by year-month
          const yearMonthA = a.replace('-', '');
          const yearMonthB = b.replace('-', '');
          return yearMonthA.localeCompare(yearMonthB);
        });
      })
      .addCase(callForDynamicFilterMonthCalendar.rejected, (state, action) => {
        state.monthCalendarLoading = false;
        state.monthCalendarError = action.payload as string;
      })
      .addCase(fetchARNPricingForAIMSResorts.pending, () => {
        // Handle loading state if needed
      })
      .addCase(fetchARNPricingForAIMSResorts.fulfilled, (state, action) => {
        if (
          action.payload &&
          typeof action.payload === 'object' &&
          'aiHotelDetails' in action.payload &&
          Array.isArray((action.payload).aiHotelDetails)
        ) {
          (action.payload).aiHotelDetails.forEach((updatedItem) => {
            const index = state.resortData.findIndex(item => item.id === updatedItem.rciResortCode);
            if (index !== -1) {
              state.resortData[index] = { ...state.resortData[index], aimsPrice: updatedItem.price };
            }
          });
        }
      })
      .addCase(fetchARNPricingForAIMSResorts.rejected, () => {
        // state.aimsError = action.payload;
        // state.aimsStatus = 'failed';
      })
      // .addCase(syncSearchCriteria.pending, (state) => {
      //   // Handle loading state if needed
      // })
      // .addCase(syncSearchCriteria.fulfilled, (state) => {
      //   console.log('syncSearchCriteria completed successfully');
      // })
      // .addCase(syncSearchCriteria.rejected, (state, action) => {
      //   console.warn('syncSearchCriteria failed:', action.payload);
      //   // Handle error state if needed
      // });
  },
});
 
// Export actions
export const { clearResortData, setLoading, clearError, setLoadingMore, clearWishlistError, clearAllowedDates, clearAllowedMonths, setTabFlags } = listViewSlice.actions;
 
// ✅ Helper function to safely get nested object properties (replaces lodash get)
// function safeGet(obj: any, path: string, defaultValue: any = undefined): any {
//   const keys = path.split('.');
//   let current = obj;
  
//   for (const key of keys) {
//     if (current === null || current === undefined || typeof current !== 'object') {
//       return defaultValue;
//     }
//     current = current[key];
//   }
  
//   return current !== undefined ? current : defaultValue;
// }

// ✅ Add the getOfferDetailsDirect async thunk (add this after fetchLonLatData function)
// ✅ Simple async function (not a thunk)
// ✅ Fix: Change return type from OfferDetailsResponse to OfferDetail[]
export const getOfferDetailsDirect = async (): Promise<OfferDetailsResponse> => {
  try {      
    // console.log('Fetching offer details directly from API...');
    
    const offersUrl = configuredAEMUrl(GET_OFFER_DETAILS);

    const response = await invokeTypedService<OfferDetailsResponse>({
      resource: offersUrl,
      method: 'GET',
      replaceUrlWithResource: true,
    });

    if (response.status === 200 && response.data) {
      // ✅ Extract the array from the response
      const offerDetailsData = response.data.offerDetails || { offersList: [], rentalDataJsons: null };
      
      // ✅ Store to localStorage if RS_RCIO_ES_WKS_FLAG cookie is true
      try {
        // Get cookie value
        // const { RS_WKS_FLAG } = window.weeksWebCloudConfig || {};
        const cookieValue = 'true';
        
        // Only store if cookie is true
        if (cookieValue === 'true') {
          // Get existing search_LocalStorageData or create new object
          const existingDataStr = localStorage.getItem('search_LocalStorageData');
          const existingData = existingDataStr ? JSON.parse(existingDataStr) : {};
          
          // Add offerDetails to the object
          const updatedData = {
            ...existingData,
            offerDetails: offerDetailsData
          };
          
          // Store back to localStorage
          localStorage.setItem('search_LocalStorageData', JSON.stringify(updatedData));
          
          // console.log('✅ Offer details stored in localStorage:', offerDetailsData);
        } else {
          // console.log('ℹ️ RS_RCIO_ES_WKS_FLAG cookie is not true, skipping localStorage storage');
        }
      } catch (storageError) {
        console.error('❌ Error storing to localStorage:', storageError);
      }
      
      // ✅ Return the array, not the whole response object
      return response.data;
    } else {
      throw new Error(`Failed to fetch offer details: ${response.status}`);
    }
  } catch (error) {
    console.error('Error fetching offer details:', error);
    throw error;
  }
};

// Export reducer
export default listViewSlice.reducer;
 
// ListViewState is already exported above with the interface declaration